import { createClientConfig } from "@repo/design-system/setup/create-client-config";
import { env } from "./env";

export type Client =
  | typeof env.CLIENT
  | "econ"
  | "eon-gridsolutions"
  | "controlsolutions"
  | "eco2solar"
  | "edg-sub"
  | "westnetz"
  | "e-dis"
  | "edialog-netz"
  | "avacon"
  | "avaconwasser"
  | "bayernwerk"
  | "e-wie-einfach"
  | "westenergie"
  | "rww"
  | "hansewerk"
  | "suewag"
  | "sverige"
  | "edri"
  | "hungary"
  | "uk";

export type LanguageKey = "en" | "de" | "hu" | "sv" | "cs" | "ro";

export type ClientConfig = typeof clientConfig;
export type ClientsAndSubclients = keyof typeof clientConfig;

export const clientConfig = createClientConfig<Client, LanguageKey>({
  eon: {
    defaultLocale: "en",
    locales: ["de", "en", "ro", "hu", "sv", "cs"],
  },
  econ: {
    defaultLocale: "de",
    locales: ["de", "en"],
  },
  "eon-gridsolutions": {
    defaultLocale: "de",
    locales: ["de"],
  },
  hungary: {
    defaultLocale: "hu",
    locales: ["hu"],
    hostNames: ["allas.eon.hu"],
  },
  sverige: {
    defaultLocale: "sv",
    locales: ["sv", "en"],
    hostNames: ["jobs.eon.se"],
  },
  // Order matters: for a bare hostname the middleware resolves the first
  // client listing it, so `uk` must precede the UK companies that share its
  // domain. controlsolutions and eco2solar keep their client segment on that
  // host (/en/controlsolutions/job/...) so their own template applies.
  uk: {
    defaultLocale: "en",
    locales: ["en"],
    hostNames: ["jobs.eon-uk-careers.com"],
    legacyRedirect: false,
    redirectToCanonicalHost: true,
  },
  controlsolutions: {
    defaultLocale: "en",
    locales: ["en", "de"],
    hostNames: ["jobs.eon-uk-careers.com"],
    legacyRedirect: false,
    redirectToCanonicalHost: true,
  },
  eco2solar: {
    defaultLocale: "en",
    locales: ["en", "de"],
    hostNames: ["jobs.eon-uk-careers.com"],
    legacyRedirect: false,
    redirectToCanonicalHost: true,
  },
  "edg-sub": {
    defaultLocale: "de",
    locales: ["de"],
  },
  westnetz: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.westnetz.de"],
    legacyRedirect: true,
  },
  "e-dis": {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.e-dis.de"],
    legacyRedirect: true,
  },
  "edialog-netz": {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.edialog-netz.de"],
    legacyRedirect: true,
  },
  avacon: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.avacon.de"],
  },
  avaconwasser: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.avacon.de"],
  },
  edri: {
    defaultLocale: "de",
    locales: ["de", "en"],
  },
  bayernwerk: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.bayernwerk.de"],
    legacyRedirect: true,
  },
  "e-wie-einfach": {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.e-wie-einfach.de"],
    legacyRedirect: true,
  },
  westenergie: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.westenergie.de"],
    legacyRedirect: true,
  },
  rww: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.rww.de"],
    legacyRedirect: true,
  },
  hansewerk: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.hansewerk.com"],
    legacyRedirect: true,
  },
  suewag: {
    defaultLocale: "de",
    locales: ["de"],
    hostNames: ["jobs.suewag.com"],
    legacyRedirect: true,
  },
});

/**
 * Safely gets client configuration with fallback to default client
 * @param subclient - The subclient key to look up
 * @returns The client configuration, falling back to env.CLIENT if subclient not found
 */
export function getClientConfig(
  subclient: string
): NonNullable<ClientConfig[keyof ClientConfig]> {
  const fallbackConfig = clientConfig[env.CLIENT as keyof ClientConfig];

  // env.CLIENT is guaranteed to be valid at runtime due to t3-oss validation
  if (!fallbackConfig) {
    throw new Error(`Invalid CLIENT environment variable: ${env.CLIENT}`);
  }

  return clientConfig[subclient as keyof ClientConfig] ?? fallbackConfig;
}

/**
 * Get all client keys from the config
 */
export function getAllClientKeys(): (keyof ClientConfig)[] {
  return Object.keys(clientConfig) as (keyof ClientConfig)[];
}
